/**
 * The wallet marks on the card tile of the top-up method picker.
 *
 * Stripe's hosted checkout offers Apple Pay, Google Pay and Link under the
 * card option, and in the payment methods report they are more than a third
 * of all top-ups (Link alone a third). The checkout offers a wallet only where
 * it can be used, so each mark is gated on what this page can learn about the
 * device:
 *
 * - Apple Pay where the browser itself says it can pay with Apple Pay;
 * - Google Pay in Chrome, and on Android in any browser. Other Chromium
 *   browsers on the desktop (Edge, Opera) are left out: they carry "Chrome/"
 *   in their user agent but are not where Google Pay keeps its cards;
 * - Link everywhere: it recognises the customer by email, not by device.
 *
 * What this page cannot learn is whether the customer has a card saved in the
 * wallet, which the checkout needs as well. Google's `isReadyToPay()` would
 * tell for Google Pay, at the cost of loading Google's script for every
 * visitor to the billing page; Apple's `canMakePaymentsWithActiveCard()`
 * needs the merchant identifier Stripe holds, so Apple Pay has no such check
 * here at all. These are therefore acceptance marks for a device that
 * supports the wallet, not a promise that the checkout will show it. A
 * customer with nothing saved pays by card on the same page. Google requires
 * the readiness check before its payment button, not before an acceptance
 * mark in a list of methods.
 */
export type WalletMark = 'applePay' | 'googlePay' | 'link';

export function walletMarks(device: {
  /** `ApplePaySession.canMakePayments()`, false where there is no such API. */
  applePay: boolean;
  userAgent: string;
  /** `navigator.userAgentData.brands`, empty where the browser has none. */
  brands: readonly string[];
}): WalletMark[] {
  const android = /Android/i.test(device.userAgent);
  const chrome =
    device.brands.includes('Google Chrome') ||
    (device.brands.length === 0 &&
      /Chrome\/\d/.test(device.userAgent) &&
      !/Edg\/|OPR\/|SamsungBrowser\/|YaBrowser\//.test(device.userAgent));

  const marks: WalletMark[] = [];
  if (device.applePay) marks.push('applePay');
  if (chrome || android) marks.push('googlePay');
  marks.push('link');
  return marks;
}

/** Reads the device signals `walletMarks` takes from the running browser. */
export function readWalletDevice(): Parameters<typeof walletMarks>[0] {
  let applePay = false;
  try {
    // Throws outside a secure top-level context; absent outside WebKit.
    const session = (window as { ApplePaySession?: { canMakePayments(): boolean } }).ApplePaySession;
    applePay = session?.canMakePayments() === true;
  } catch {
    applePay = false;
  }
  const uaData = (navigator as { userAgentData?: { brands?: { brand: string }[] } }).userAgentData;
  return {
    applePay,
    userAgent: navigator.userAgent,
    brands: uaData?.brands?.map((b) => b.brand) ?? [],
  };
}
